/**
 * FeedbackSidebar — displays multi-source feedback pasted during onboarding.
 *
 * Three sections: Self-Review, Performance Review Feedback, Anytime Feedback.
 * Content is auto-parsed from the raw paste on open.
 * Managers can toggle to raw edit mode to correct/update the paste.
 */
import { useMemo, useState } from 'react';
import { C } from '../../data/managerReviewData';
import {
  parseSelfReview, parseMultiReviewerFeedback, cleanCultureAmpPaste,
  type ParsedReviewerBlock, type ParsedQA,
} from '../../utils/cultureAmpParser';

const CULTURE_AMP_URL = 'https://finn.eu.cultureamp.com/app/auth?redirect=https%3A%2F%2Ffinn.eu.cultureamp.com%2Fapp%2Fhome';

interface FeedbackSidebarProps {
  selfReview: string;
  setSelfReview: (v: string) => void;
  perfFeedbackRaw: string;
  setPerfFeedbackRaw: (v: string) => void;
  anytimeFeedbackRaw: string;
  setAnytimeFeedbackRaw: (v: string) => void;
  employeeName?: string;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function QAList({ qas }: { qas: ParsedQA[] }) {
  if (!qas.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {qas.map((qa, i) => (
        <div key={i}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#1a1a1a', lineHeight: 1.5, marginBottom: 2 }}>
            {qa.question}
          </div>
          {qa.subInstruction && (
            <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.5, marginBottom: 4, fontStyle: 'italic' }}>
              {qa.subInstruction}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#444', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {qa.answer || <span style={{ color: '#ccc' }}>(no answer)</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewerCard({ block, showType = false, fallbackName }: { block: ParsedReviewerBlock; showType?: boolean; fallbackName?: string }) {
  const displayName = block.reviewer || fallbackName || '';
  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div style={{ border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
      {/* Reviewer header */}
      {(block.reviewer || block.date || fallbackName) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f5f5f5', borderBottom: '1px solid #ebebeb' }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', background: '#e0e0e0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>{initials}</span>
          </div>
          <div style={{ flex: 1 }}>
            {displayName && (
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{displayName}</div>
            )}
            {block.date && (
              <div style={{ fontSize: 11, color: '#aaa' }}>{block.date}</div>
            )}
          </div>
          {showType && block.type !== 'Self' && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
              color: C.blue, background: C.lightBlue,
            }}>{block.type}</span>
          )}
        </div>
      )}
      {/* Q&A body */}
      <div style={{ padding: '12px 14px' }}>
        {block.qas.length > 0
          ? <QAList qas={block.qas} />
          : (
            <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.55 }}>
              No structured questions detected. The content may use a custom format — switch to raw view to read it.
            </div>
          )
        }
      </div>
    </div>
  );
}

function RawFallback({ text }: { text: string }) {
  const cleaned = cleanCultureAmpPaste(text);
  return (
    <div style={{ fontSize: 12, color: '#444', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 14px' }}>
      {cleaned || <span style={{ color: '#ccc' }}>(empty)</span>}
    </div>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────────

function FeedbackSection({
  title, raw, setRaw, parsed, placeholder, emptyMessage, reviewerFallback,
}: {
  title: string;
  raw: string;
  setRaw: (v: string) => void;
  parsed: ParsedReviewerBlock[] | ParsedReviewerBlock | null;
  placeholder: string;
  emptyMessage: string;
  reviewerFallback?: string;
}) {
  const [editing, setEditing] = useState(false);

  const blocks: ParsedReviewerBlock[] = parsed === null
    ? []
    : Array.isArray(parsed)
      ? parsed
      : [parsed];

  const hasContent = raw.trim().length > 0;
  const hasParsed = blocks.length > 0 && (blocks.some(b => b.qas.length > 0 || b.reviewer || b.date));

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const cleaned = cleanCultureAmpPaste(e.clipboardData.getData('text'));
    const el = e.currentTarget;
    const next = raw.substring(0, el.selectionStart) + cleaned + raw.substring(el.selectionEnd);
    setRaw(next);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.09em' }}>{title}</div>
        {hasContent && (
          <button
            onClick={() => setEditing(e => !e)}
            style={{ fontSize: 11, color: editing ? C.blue : '#aaa', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter', padding: 0 }}
          >
            {editing ? '✓ Done editing' : '✎ Edit raw'}
          </button>
        )}
      </div>

      {/* Edit mode */}
      {(editing || !hasContent) && (
        <div>
          <textarea
            value={raw}
            onChange={e => setRaw(e.target.value)}
            onPaste={handlePaste}
            placeholder={placeholder}
            autoFocus={editing}
            style={{
              width: '100%', minHeight: 100, borderRadius: 8, border: '1.5px solid #d1d5db',
              padding: '10px 12px', fontSize: 12, fontFamily: 'Inter',
              resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55, outline: 'none', color: '#333',
            }}
          />
          {raw.trim() && editing && (
            <button
              onClick={() => setEditing(false)}
              style={{ marginTop: 6, fontSize: 11, color: C.blue, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter', padding: 0 }}
            >
              ✓ Done editing
            </button>
          )}
          {!hasContent && (
            <div style={{ fontSize: 11, color: '#ccc', marginTop: 4 }}>{emptyMessage}</div>
          )}
        </div>
      )}

      {/* Parsed view */}
      {!editing && hasContent && (
        hasParsed
          ? blocks.map((block, i) => (
              <ReviewerCard key={i} block={block} showType={blocks.length > 1} fallbackName={reviewerFallback} />
            ))
          : <RawFallback text={raw} />
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function FeedbackSidebar({
  selfReview,
  setSelfReview,
  perfFeedbackRaw,
  setPerfFeedbackRaw,
  anytimeFeedbackRaw,
  setAnytimeFeedbackRaw,
  employeeName,
}: FeedbackSidebarProps) {
  // Parse once per raw-value change
  const parsedSelf = useMemo(() => parseSelfReview(selfReview), [selfReview]);
  const parsedPerf = useMemo(() => parseMultiReviewerFeedback(perfFeedbackRaw, 'Peer'), [perfFeedbackRaw]);
  const parsedAnytime = useMemo(() => parseMultiReviewerFeedback(anytimeFeedbackRaw, 'Peer'), [anytimeFeedbackRaw]);

  const anyContent = selfReview.trim() || perfFeedbackRaw.trim() || anytimeFeedbackRaw.trim();

  return (
    <div style={{ padding: '20px 20px 40px' }}>
      {/* Header */}
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#aaa' }}>Pasted from Culture Amp</span>
        <a href={CULTURE_AMP_URL} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, color: C.blue, fontWeight: 600, textDecoration: 'none' }}>
          Open Culture Amp ↗
        </a>
      </div>

      {!anyContent && (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#ccc', marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.6 }}>
            No feedback pasted yet. Go back to the "Before you start" step to add it.
          </div>
        </div>
      )}

      {/* Self-Review */}
      <FeedbackSection
        title="Self-Review"
        raw={selfReview}
        setRaw={setSelfReview}
        parsed={parsedSelf}
        placeholder="Paste the self-review from Culture Amp here…"
        emptyMessage="No self-review pasted."
        reviewerFallback={employeeName}
      />

      {/* Peer & Upward Feedback */}
      <FeedbackSection
        title="Peer & Upward Feedback"
        raw={perfFeedbackRaw}
        setRaw={setPerfFeedbackRaw}
        parsed={parsedPerf}
        placeholder="Paste all peer and upward feedback here — multiple reviewers supported."
        emptyMessage="No peer & upward feedback pasted."
      />

      {/* Anytime Feedback */}
      <FeedbackSection
        title="Anytime Feedback"
        raw={anytimeFeedbackRaw}
        setRaw={setAnytimeFeedbackRaw}
        parsed={parsedAnytime}
        placeholder="Paste anytime feedback here — multiple reviewers supported."
        emptyMessage="No anytime feedback pasted."
      />
    </div>
  );
}
